import { View, Alert } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Button } from '@/components/ui/Button';
import { useTheme } from '@/theme';
import { copyToClipboard } from '@/utils/clipboard';
import { buildGoogleMapsSearchUrl, openDirectionsToCoordinate } from '@/utils/openExternalMaps';
import type { LocationCoordinates } from '@/services/location';

type ShareLocationActionsRowProps = {
  coordinate: LocationCoordinates;
  /** Used for accessibility labels (e.g. sharer name). */
  label: string;
};

export function ShareLocationActionsRow({ coordinate, label }: ShareLocationActionsRowProps) {
  const theme = useTheme();

  const handleCopy = async () => {
    try {
      await copyToClipboard(buildGoogleMapsSearchUrl(coordinate));
      Alert.alert('Copied', 'Maps link copied to clipboard');
    } catch {
      Alert.alert('Copy failed', 'Unable to copy link.');
    }
  };

  const handleDirections = () => {
    void openDirectionsToCoordinate(coordinate);
  };

  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.spacing(0.75),
        marginTop: theme.spacing(0.75),
      }}
    >
      <Button
        label="Directions"
        variant="ghost"
        size="small"
        onPress={handleDirections}
        icon={<Ionicons name="navigate-outline" size={18} color={theme.colors.primary} />}
        accessibilityLabel={`Directions to ${label}`}
        accessibilityHint="Opens maps with this location as the destination"
      />
      <Button
        label="Copy link"
        variant="ghost"
        size="small"
        onPress={handleCopy}
        icon={<Ionicons name="copy-outline" size={18} color={theme.colors.primary} />}
        accessibilityLabel={`Copy maps link for ${label}`}
        accessibilityHint="Copies a Google Maps link to the clipboard"
      />
    </View>
  );
}
